import { forwardRef, InputHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/utils';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  error?: boolean;
  inputSize?: 'sm' | 'default' | 'lg';
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, error, inputSize = 'default', leftIcon, rightIcon, ...props }, ref) => {
    // Size variant styles
    const sizeStyles = {
      sm: 'h-[var(--input-height-sm)] px-[var(--spacing-md)] text-[var(--font-size-sm)]',
      default: 'h-[var(--input-height-md)] px-[var(--spacing-md)] text-[var(--font-size-base)]',
      lg: 'h-[var(--input-height-lg)] px-[var(--spacing-md)] text-[var(--font-size-base)]',
    };

    // Icon padding adjustments
    const iconPaddingStyles = {
      sm: {
        left: leftIcon ? 'pl-9' : '',
        right: rightIcon ? 'pr-9' : '',
      },
      default: {
        left: leftIcon ? 'pl-10' : '',
        right: rightIcon ? 'pr-10' : '',
      },
      lg: {
        left: leftIcon ? 'pl-11' : '',
        right: rightIcon ? 'pr-11' : '',
      },
    };

    return (
      <div className="relative w-full">
        {leftIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-tertiary)] pointer-events-none">
            {leftIcon}
          </div>
        )}
        <input
          type={type}
          aria-invalid={error ? 'true' : undefined}
          className={cn(
            // Base styles
            'flex w-full rounded-[var(--radius-md)] border bg-[var(--color-surface-primary)]',
            'text-[var(--color-text-primary)] placeholder:text-[var(--color-text-secondary)]',
            'font-[var(--font-weight-regular)]',
            'transition-all duration-[var(--duration-fast)] ease-[var(--easing-default)]',

            // Default border
            'border-[var(--color-border-default)]',

            // File input styles
            'file:border-0 file:bg-transparent file:text-[var(--font-size-base)] file:font-[var(--font-weight-medium)]',

            // Hover state
            'hover:border-[var(--color-border-strong)]',

            // Focus state (brand focus)
            'focus-visible:outline-none focus-visible:border-[var(--color-accent)] focus-visible:shadow-[var(--shadow-glow)]',

            // Disabled state
            'disabled:cursor-not-allowed disabled:bg-[var(--color-surface-secondary)] disabled:text-[var(--color-text-disabled)] disabled:border-[var(--color-border-default)]',

            // Error state
            error && [
              'border-[var(--color-error)]',
              'focus-visible:border-[var(--color-error)]',
              'focus-visible:shadow-[0_0_20px_rgba(255,59,48,0.3)]',
            ],

            // Size variants
            sizeStyles[inputSize],

            // Icon padding
            iconPaddingStyles[inputSize].left,
            iconPaddingStyles[inputSize].right,

            className
          )}
          ref={ref}
          {...props}
        />
        {rightIcon && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-text-tertiary)] pointer-events-none">
            {rightIcon}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
